import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Settings, MessageSquare, Tag, Edit2, Check, X } from 'lucide-react';

function SectionHeader({ title, icon, isOpen, onToggle, count, colorClass }) {
  return (
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${colorClass}`}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        {title}
        {count > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-white border font-medium">
            {count}
          </span>
        )}
      </div>
      {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
    </button>
  );
}

export default function StepVarEditor({ stepVar, onChange, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({ params: false, templates: false, tags: false });
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');

  const toggleSection = (key) => setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));

  const startEditName = (e) => {
    e.stopPropagation();
    setTempName(stepVar.Step_Var_Name || '');
    setEditingName(true);
  };

  const saveEditName = () => {
    onChange({ ...stepVar, Step_Var_Name: tempName });
    setEditingName(false);
  };

  const cancelEditName = () => {
    setEditingName(false);
  };

  const updateField = (field, value) => {
    onChange({ ...stepVar, [field]: value });
  };

  const updateTaskSettings = (field, value) => {
    onChange({
      ...stepVar,
      Task_Settings: {
        ...stepVar.Task_Settings,
        [field]: value
      }
    });
  };

  const addTemplate = () => {
    const templates = stepVar.Task_Settings?.Templates || [];
    updateTaskSettings('Templates', [
      ...templates,
      { WABA_ID: null, Template: '', Choose_If: '', Filling_Rules: null }
    ]);
  };

  const updateTemplate = (index, field, value) => {
    const templates = [...(stepVar.Task_Settings?.Templates || [])];
    templates[index] = { ...templates[index], [field]: value };
    updateTaskSettings('Templates', templates);
  };

  const deleteTemplate = (index) => {
    const templates = (stepVar.Task_Settings?.Templates || []).filter((_, i) => i !== index);
    updateTaskSettings('Templates', templates);
  };

  const addQualTag = () => {
    const qualTags = stepVar.Task_Settings?.Qual_Tags || [];
    updateTaskSettings('Qual_Tags', [...qualTags, { Qual_Tag: '', Choose_If: '' }]);
  };

  const updateQualTag = (index, field, value) => {
    const qualTags = [...(stepVar.Task_Settings?.Qual_Tags || [])];
    qualTags[index] = { ...qualTags[index], [field]: value };
    updateTaskSettings('Qual_Tags', qualTags);
  };

  const deleteQualTag = (index) => {
    const qualTags = (stepVar.Task_Settings?.Qual_Tags || []).filter((_, i) => i !== index);
    updateTaskSettings('Qual_Tags', qualTags);
  };

  const addModulTag = () => {
    const modulTags = stepVar.Task_Settings?.Modul_Tags || [];
    updateTaskSettings('Modul_Tags', [...modulTags, { Modul_Tag: null, Choose_If: null }]);
  };

  const updateModulTag = (index, field, value) => {
    const modulTags = [...(stepVar.Task_Settings?.Modul_Tags || [])];
    modulTags[index] = { ...modulTags[index], [field]: value };
    updateTaskSettings('Modul_Tags', modulTags);
  };

  const deleteModulTag = (index) => {
    const modulTags = (stepVar.Task_Settings?.Modul_Tags || []).filter((_, i) => i !== index);
    updateTaskSettings('Modul_Tags', modulTags);
  };

  const templateCount = (stepVar.Task_Settings?.Templates || []).filter(Boolean).length;
  const qualTagCount = (stepVar.Task_Settings?.Qual_Tags || []).filter(Boolean).length;
  const modulTagCount = (stepVar.Task_Settings?.Modul_Tags || []).filter(Boolean).length;
  const tagCount = qualTagCount + modulTagCount;

  const hasParams = !!(
    stepVar.Task_Settings?.Status ||
    stepVar.Task_Settings?.Time_Wait_CL_Reply ||
    stepVar.Task_Settings?.Group_Followup_Or_Stop_If_No_Reply
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-500 hover:text-blue-600 shrink-0 transition-colors"
        >
          {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>

        {editingName ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              autoFocus
              type="text"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEditName(); if (e.key === 'Escape') cancelEditName(); }}
              className="flex-1 min-w-0 px-2 py-0.5 text-sm border border-blue-400 rounded outline-none focus:ring-1 focus:ring-blue-200"
            />
            <button onClick={saveEditName} className="text-green-600 hover:bg-green-50 p-0.5 rounded transition-colors"><Check size={14} /></button>
            <button onClick={cancelEditName} className="text-gray-400 hover:bg-gray-100 p-0.5 rounded transition-colors"><X size={14} /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-700 truncate">{stepVar.Step_Var_Name || 'Вариант шага'}</span>
            <button
              onClick={startEditName}
              className="shrink-0 text-gray-400 hover:text-blue-500 p-0.5 rounded transition-colors"
              title="Переименовать вариант"
            >
              <Edit2 size={12} />
            </button>
            <div className="flex gap-1 ml-1 shrink-0">
              {templateCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full font-medium">
                  {templateCount} шаб.
                </span>
              )}
              {tagCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                  {tagCount} тег.
                </span>
              )}
              {hasParams && (
                <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full font-medium">
                  cfg
                </span>
              )}
            </div>
          </div>
        )}

        <button
          onClick={onDelete}
          className="shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-colors"
          title="Удалить вариант"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* ── Choose_If + Task — всегда видимы ─────────────────── */}
      <div className="px-3 pt-3 pb-2 space-y-2.5 border-b border-gray-100">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-blue-700 mb-1.5 uppercase tracking-wide">
            Choose_If — условие выбора
          </label>
          <textarea
            value={stepVar.Choose_If || ''}
            onChange={(e) => updateField('Choose_If', e.target.value)}
            className="w-full px-2 py-1.5 border border-blue-200 rounded text-xs bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none transition"
            rows={2}
            placeholder="Когда выбирать этот вариант…"
          />
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">
            Task — задача для AI
          </label>
          <textarea
            value={stepVar.Task_Settings?.Task || ''}
            onChange={(e) => updateTaskSettings('Task', e.target.value)}
            className="w-full px-2 py-1.5 border border-indigo-200 rounded text-xs bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
            rows={4}
            placeholder="Что должен сделать AI агент на этом шаге…"
          />
        </div>
      </div>

      {expanded && (
        <div className="p-3 space-y-3">
          {/* ── Параметры (сворачивается) ───────────────────────── */}
          <div>
            <SectionHeader
              title="Параметры"
              icon={<Settings size={12} />}
              isOpen={sectionsOpen.params}
              onToggle={() => toggleSection('params')}
              count={hasParams ? 1 : 0}
              colorClass="text-gray-700 bg-gray-100 border-gray-200 hover:bg-gray-200"
            />
            {sectionsOpen.params && (
              <div className="mt-2 space-y-2 pl-1">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Статус</label>
                    <input
                      type="text"
                      value={stepVar.Task_Settings?.Status || ''}
                      onChange={(e) => updateTaskSettings('Status', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-blue-400 outline-none transition"
                      placeholder="greeting"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Время ожидания</label>
                    <input
                      type="text"
                      value={stepVar.Task_Settings?.Time_Wait_CL_Reply || ''}
                      onChange={(e) => updateTaskSettings('Time_Wait_CL_Reply', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-blue-400 outline-none transition"
                      placeholder="24h"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Follow-up группа</label>
                  <input
                    type="text"
                    value={stepVar.Task_Settings?.Group_Followup_Or_Stop_If_No_Reply || ''}
                    onChange={(e) => updateTaskSettings('Group_Followup_Or_Stop_If_No_Reply', e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-blue-400 outline-none transition"
                    placeholder="1 или #Stop"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Templates (сворачивается) ──────────────────────── */}
          <div>
            <SectionHeader
              title="Templates"
              icon={<MessageSquare size={12} />}
              isOpen={sectionsOpen.templates}
              onToggle={() => {
                if (!sectionsOpen.templates && templateCount === 0) {
                  addTemplate();
                }
                toggleSection('templates');
              }}
              count={templateCount}
              colorClass="text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
            />
            {sectionsOpen.templates && (
              <div className="mt-2 space-y-2">
                {stepVar.Task_Settings?.Templates?.map((template, idx) => {
                  if (!template) return null;
                  return (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-1.5">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-500">Шаблон {idx + 1}</span>
                        <button
                          onClick={() => deleteTemplate(idx)}
                          className="text-red-400 hover:text-red-600 p-0.5 rounded transition-colors"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                      <textarea
                        value={template.Template || ''}
                        onChange={(e) => updateTemplate(idx, 'Template', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-indigo-400 outline-none transition"
                        rows={2}
                        placeholder="Текст шаблона"
                      />
                      <input
                        type="text"
                        value={template.Choose_If || ''}
                        onChange={(e) => updateTemplate(idx, 'Choose_If', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-indigo-400 outline-none transition"
                        placeholder="Choose_If"
                      />
                      <input
                        type="text"
                        value={template.WABA_ID || ''}
                        onChange={(e) => updateTemplate(idx, 'WABA_ID', e.target.value || null)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-indigo-400 outline-none transition"
                        placeholder="WABA_ID (optional)"
                      />
                      <textarea
                        value={template?.Filling_Rules || ''}
                        onChange={(e) => updateTemplate(idx, 'Filling_Rules', e.target.value || null)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-indigo-400 outline-none transition"
                        rows={1}
                        placeholder="Filling_Rules (optional)"
                      />
                    </div>
                  );
                })}
                <button
                  onClick={addTemplate}
                  className="w-full text-xs text-indigo-600 hover:bg-indigo-50 border border-indigo-200 border-dashed px-2 py-1.5 rounded-md flex items-center justify-center gap-1 transition-colors"
                >
                  <Plus size={12} /> Добавить шаблон
                </button>
              </div>
            )}
          </div>

          {/* ── Теги (сворачивается) ───────────────────────────── */}
          <div>
            <SectionHeader
              title="Теги"
              icon={<Tag size={12} />}
              isOpen={sectionsOpen.tags}
              onToggle={() => toggleSection('tags')}
              count={tagCount}
              colorClass="text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
            />
            {sectionsOpen.tags && (
              <div className="mt-2 space-y-3">
                {/* Qual_Tags */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-600">Qual_Tags</span>
                    <button
                      onClick={addQualTag}
                      className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-0.5 rounded flex items-center gap-1 transition-colors"
                    >
                      <Plus size={10} /> Добавить
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {stepVar.Task_Settings?.Qual_Tags?.map((tag, idx) => {
                      if (!tag) return null;
                      return (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={tag.Qual_Tag || ''}
                            onChange={(e) => updateQualTag(idx, 'Qual_Tag', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:border-emerald-400 outline-none transition"
                            placeholder="Qual_Tag"
                          />
                          <input
                            type="text"
                            value={tag.Choose_If || ''}
                            onChange={(e) => updateQualTag(idx, 'Choose_If', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:border-emerald-400 outline-none transition"
                            placeholder="Choose_If"
                          />
                          <button
                            onClick={() => deleteQualTag(idx)}
                            className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Modul_Tags */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-600">Modul_Tags</span>
                    <button
                      onClick={addModulTag}
                      className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-0.5 rounded flex items-center gap-1 transition-colors"
                    >
                      <Plus size={10} /> Добавить
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {stepVar.Task_Settings?.Modul_Tags?.map((tag, idx) => {
                      if (!tag) return null;
                      return (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded p-2 flex items-center gap-2">
                          <input
                            type="text"
                            value={tag.Modul_Tag || ''}
                            onChange={(e) => updateModulTag(idx, 'Modul_Tag', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:border-emerald-400 outline-none transition"
                            placeholder="Modul_Tag"
                          />
                          <input
                            type="text"
                            value={tag.Choose_If || ''}
                            onChange={(e) => updateModulTag(idx, 'Choose_If', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:border-emerald-400 outline-none transition"
                            placeholder="Choose_If"
                          />
                          <button
                            onClick={() => deleteModulTag(idx)}
                            className="text-red-400 hover:text-red-600 p-1 rounded transition-colors"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
